import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  useScrollTrigger,
  Slide,
  Link,
  Button,
  Select,
  MenuItem,
  FormControl,
  ListSubheader,
  TextField,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { setSelectedCountry } from "../store/slice/countrySlice";
import { logout } from "../store/slice/authSlice";
import ClientService from "../service/client.service";


function HideOnScroll({ children }: { children: React.ReactElement }) {
  const trigger = useScrollTrigger({ threshold: 50 });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function Header({ title = "CLIENT PORTAL" }: { title?: string }) {
  const [countries, setCountries] = useState<string[]>([]);
  const [allCountries, setAllCountries] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [presenceOpen, setPresenceOpen] = useState(true);
  const [allOpen, setAllOpen] = useState(true);
  const [contactNo, setContactNo] = useState<string>("NA");

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const logoSrc = "/logo.png";
  const [token, setToken] = useState<string | null>(null);
  const authState = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const cookieMatch = document.cookie.match(/(?:^|; )buyer_token=([^;]*)/);
    const cookieToken = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    const storedToken = cookieToken || localStorage.getItem('buyer_token') || sessionStorage.getItem('buyer_token');
    if (storedToken && storedToken !== 'null' && storedToken !== 'undefined' && storedToken !== 'false') {
      setToken(storedToken);
    } else {
      setToken(null);
    }
  }, [authState.isAuthenticated]);

  const isLoggedIn = Boolean(token || authState.isAuthenticated);

  const handleAuthAction = () => {
    if (isLoggedIn) {
      localStorage.removeItem('buyer_token');
      sessionStorage.removeItem('buyer_token');
      const domain = window.location.hostname.includes("sourceseas.com") ? ".sourceseas.com" : window.location.hostname;
      document.cookie = `buyer_token=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      dispatch(logout());
      setToken(null);
      navigate('/login');
      return;
    }
    navigate('/login');
  };

  const handleCountryChange = (event: { target: { value: string | number | string[] | null } }) => {
    const newCountry = String(event.target.value);
    if (newCountry && newCountry !== 'null' && newCountry !== 'undefined') {
      dispatch(setSelectedCountry(newCountry));
    }
  };

  const selectedCountry = useSelector((state: any) => state.country.selectedCountry);

  useEffect(() => {
    const fetchContact = async () => {
      if (selectedCountry) {
        try {
          const res = await ClientService.getContactNo(selectedCountry);
          let responseData = res?.data?.data || res?.data;

          // If the data is an array, take the first item
          if (Array.isArray(responseData) && responseData.length > 0) {
            responseData = responseData[0];
          }

          const number = responseData?.contactNo || responseData?.phone || responseData?.contact_number || responseData?.number || (typeof responseData === "string" ? responseData : null);
          setContactNo(number ? number : "NA");
        } catch (error) {
          console.error("Failed to fetch contact number", error);
          setContactNo("NA");
        }
      } else {
        setContactNo("NA");
      }
    };
    fetchContact();
  }, [selectedCountry]);

  useEffect(() => {
    const getPresencesData = async () => {
      try {
        const response = await ClientService.getPresences();
        const fetchedCountries = response?.data?.countries || [];
        setCountries(fetchedCountries);

        if (fetchedCountries.length > 0 && !selectedCountry) {
          dispatch(setSelectedCountry("India"));
        }
      } catch (error: any) {
        console.log("Presences data not fetch");
      }
    };
    getPresencesData();

    const getAllCountries = async () => {
      try {
        const res = await fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
        const data = await res.json();
        const names = data.objects.countries.geometries.map((g: any) => g.properties.name).sort();
        setAllCountries(names);
      } catch (error) {
        console.log("Failed to fetch all countries");
      }
    };
    getAllCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="sticky"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1300,
            bgcolor: "secondary.main",
            color: "white",
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              display: { xs: "none", sm: "flex" },
              justifyContent: "space-between",
              minHeight: "40px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              Welcome to Sourceseas - Best Exporter
            </Typography>

            <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 4, alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LocalPhoneIcon sx={{ fontSize: 18, mr: 0.5 }} />
                {contactNo}
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Mon-Fri: 9:00am - 8:00pm
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Box sx={{ position: "sticky", top: 0, zIndex: 1200, width: "100%" }}>
        <AppBar
          position="sticky"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1200,
            bgcolor: "white",
            color: "black",
            borderBottom: "1px solid #ddd",
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              px: { xs: 1.5, sm: 2, md: 3 },
              minHeight: { xs: 64, md: 72 },
              gap: 1.5,
              flexWrap: { xs: "wrap", md: "nowrap" },
              rowGap: { xs: 1, md: 0 },
            }}
          >
            <Link
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
                flex: { xs: "1 1 100%", md: "0 0 auto" },
                justifyContent: { xs: "center", md: "flex-start" },
                minWidth: 0,
              }}
            >
              <img
                src={logoSrc}
                alt="logo"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "50px",
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  letterSpacing: 0.5,
                  fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.1rem" },
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
            </Link>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                width: { xs: "100%", md: "auto" },
              }}
            >
              <FormControl size="small" sx={{ minWidth: 120, maxWidth: { xs: 180, md: 220 } }}>
                <Select
                  value={selectedCountry || ""}
                  onChange={handleCountryChange}
                  displayEmpty
                  sx={{
                    height: 36,
                    bgcolor: "white",
                    fontSize: "14px",
                    fontWeight: 500
                  }}
                >
                  <MenuItem disableRipple sx={{ '&:hover': { bgcolor: 'transparent' }, cursor: 'default', py: 1 }} onKeyDownCapture={(e: React.KeyboardEvent<HTMLElement>) => e.stopPropagation()} onClickCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                    <TextField size="small" placeholder="Search Country..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.stopPropagation()} fullWidth />
                  </MenuItem>
                  <ListSubheader
                    sx={{ fontWeight: 700, lineHeight: '30px', bgcolor: '#f5f5f5', color: 'primary.main', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClickCapture={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); e.preventDefault(); setPresenceOpen(prev => !prev); }}
                    onMouseDownCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                  >
                    Presence Country {presenceOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </ListSubheader>
                  {presenceOpen && (Array.isArray(countries) ? countries : []).filter(c => c.toLowerCase().includes(searchQuery.toLowerCase())).map((c) => (
                    <MenuItem key={`presence-${c}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                  <ListSubheader
                    sx={{ fontWeight: 700, lineHeight: '30px', bgcolor: '#f5f5f5', color: 'primary.main', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClickCapture={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); e.preventDefault(); setAllOpen(prev => !prev); }}
                    onMouseDownCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                  >
                    All Country {allOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </ListSubheader>
                  {allOpen && allCountries.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase())).map((c) => (
                    <MenuItem key={`all-${c}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleAuthAction}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.5,
                  borderRadius: '8px',
                  minWidth: 96,
                }}
              >
                {isLoggedIn ? 'Logout' : 'Login'}
              </Button>
            </Box>

            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1, width: "100%", justifyContent: "space-between" }}>
              <FormControl size="small" sx={{ minWidth: 90, flex: 1 }}>
                <Select
                  value={selectedCountry || ""}
                  onChange={handleCountryChange}
                  displayEmpty
                  sx={{
                    height: 30,
                    bgcolor: "white",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  <MenuItem disableRipple sx={{ '&:hover': { bgcolor: 'transparent' }, cursor: 'default', py: 1 }} onKeyDownCapture={(e: React.KeyboardEvent<HTMLElement>) => e.stopPropagation()} onClickCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                    <TextField size="small" placeholder="Search Country..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.stopPropagation()} fullWidth />
                  </MenuItem>
                  <ListSubheader
                    sx={{ fontWeight: 700, lineHeight: '30px', bgcolor: '#f5f5f5', color: 'primary.main', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClickCapture={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); e.preventDefault(); setPresenceOpen(prev => !prev); }}
                    onMouseDownCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                  >
                    Presence Country {presenceOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </ListSubheader>
                  {presenceOpen && (Array.isArray(countries) ? countries : []).filter(c => c.toLowerCase().includes(searchQuery.toLowerCase())).map((c) => (
                    <MenuItem key={`presence-${c}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                  <ListSubheader
                    sx={{ fontWeight: 700, lineHeight: '30px', bgcolor: '#f5f5f5', color: 'primary.main', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClickCapture={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); e.preventDefault(); setAllOpen(prev => !prev); }}
                    onMouseDownCapture={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                  >
                    All Country {allOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </ListSubheader>
                  {allOpen && allCountries.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase())).map((c) => (
                    <MenuItem key={`all-${c}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleAuthAction}
                sx={{
                  minWidth: 78,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 1,
                }}
              >
                {isLoggedIn ? 'Logout' : 'Login'}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </Box>
    </>
  );
}